/**
 * A categorical TONE — which of a look's colour slots an item occupies.
 *
 * Task 0178. The thing this exists to replace is a colour chosen by an
 * item's POSITION: Fibery's sidebar picked a space's colour with
 * `:nth-of-type(6n+1)` through `(6n+6)`, and Notion's board picks a lane's
 * `--nt-dot`, `--nt-pill` and `--nt-tint` with `:nth-child(4n+…)`. With six
 * pools against a cycle of six, every pool held a stable colour and nothing
 * looked wrong — which is the whole reason it survived. It bites on a
 * seventh item, on a hidden or filtered row (`nth-` counts what is in the
 * DOM), on any reorder including one a person does, and on `0058`
 * composition, where two looks may order one rail differently and the same
 * pool then wears two colours.
 *
 * @Codex's ruling, on which `0178` was filed: *"A workspace/collection item
 * must receive a palette value from its component input or generated
 * presentation — not from its DOM position."*
 *
 * ## A NUMBER, not a colour
 *
 * The host says which slot; each look maps its own palette across the slots
 * in tokens. A colour carried here would be one look's decision stored on
 * the data — the mistake `0173` is about — and would make a second look
 * showing the same pool wear the first look's palette.
 *
 * ## Declared beats derived, and derived beats positional
 *
 * A host that knows its items' colours declares them, because a hash cannot
 * promise distinctness and the bar is visual parity: measured on the six
 * demo pools, `fnv("pool:<id>") % 6` yields only FOUR distinct tones (films
 * and notes collide, features and sprints collide) and `% 12` yields five.
 * Shipping that would put two spaces in one colour where the reference
 * shows six — a real regression, arrived at while fixing a real defect.
 *
 * So the derivation below is the FALLBACK, for an item the host says
 * nothing about. It is worth having because it is still order-independent:
 * an undeclared seventh pool gets a stable colour of its own rather than
 * none, and never one that changes when the row above it is hidden.
 */

/** FNV-1a, 32-bit. The same hash `render-html.ts` has always used to give an
 *  enum value its hue; lifted here so the constant lives in one place. */
export const fnv1a = (text: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
};

/** How many categorical slots a tone may name. Look-independent on purpose:
 *  a look with fewer colours maps several slots onto one, and the runtime
 *  does not learn any look's palette size. */
export const TONE_SLOTS = 12;

/**
 * The tone an item occupies: its own `declared` slot when the host names
 * one, else derived from `key` — never from where the item sits.
 *
 * A declared tone is taken modulo `TONE_SLOTS` rather than refused, so a
 * host counting from 1, or past the end, still lands on a real slot instead
 * of on an attribute no rule matches.
 */
export const toneOf = (key: string, declared?: number): number =>
  (Number.isInteger(declared) ? (declared as number) % TONE_SLOTS + TONE_SLOTS : fnv1a(key)) % TONE_SLOTS;
